import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Eye, EyeOff, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import lanerunnerLogoWhite from '@/assets/lanerunner-logo-white.png';
import lanerunnerLogoBlack from '@/assets/lanerunner-logo-black.png';
import { useTheme } from 'next-themes';

export default function ResetPassword() {
  const { theme } = useTheme();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isValidToken, setIsValidToken] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [validationError, setValidationError] = useState<string | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setValidationError('No reset token provided. Please request a new password reset link.');
        setIsValidating(false);
        return;
      }

      try {
        const { data, error } = await supabase.functions.invoke('verify-reset-token', {
          body: { token }
        });

        if (error) {
          console.error('Token validation error:', error);
          setValidationError(error.message || 'Invalid or expired reset link');
          setIsValidating(false);
          return;
        }

        if (data?.valid) {
          setIsValidToken(true);
        } else {
          setValidationError(data?.error || 'Invalid or expired reset link');
        }
      } catch (err: any) {
        console.error('Token validation error:', err);
        setValidationError('Failed to validate reset link. Please try again.');
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const validatePassword = () => {
    if (password.length < 8) {
      toast({
        title: "Password too short",
        description: "Password must be at least 8 characters long.",
        variant: "destructive"
      });
      return false;
    }

    if (password !== confirmPassword) {
      toast({
        title: "Passwords don't match",
        description: "Please make sure both passwords match.",
        variant: "destructive"
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validatePassword()) return;
    if (!token) return;

    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('verify-reset-token', {
        body: { token, newPassword: password }
      });

      if (error) {
        throw new Error(error.message || 'Failed to reset password');
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to reset password');
      }

      setIsSuccess(true);
      toast({
        title: "Password reset successful!",
        description: "You can now log in with your new password."
      });

      // Redirect to login after 3 seconds.
      // Use ?mode=login so AuthPage Effect B's login-mode branch handles
      // the post-reset return cleanly (clears stale signup cache, drops any
      // partial session, shows the login form) instead of the default branch
      // which can force phone-verification for accounts with phone_verified=false.
      setTimeout(() => navigate('/auth?mode=login'), 3000);
    } catch (error: any) {
      console.error('Password reset error:', error);
      toast({
        title: "Error",
        description: error.message || 'Failed to reset password. Please try again.',
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Loading state
  if (isValidating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-lr-dark via-lr-dark-secondary to-lr-dark flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-lr-cyan mx-auto mb-4" />
          <p className="text-muted-foreground">Validating reset link...</p>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (!isValidToken && validationError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-lr-dark via-lr-dark-secondary to-lr-dark flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-card border border-border rounded-lg shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-destructive/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-destructive" />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-2">
              Invalid Reset Link
            </h1>
            <p className="text-muted-foreground mb-6">
              {validationError}
            </p>
            <Link to="/forgot-password">
              <Button variant="lr-cyan" className="w-full">
                Request New Reset Link
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-lr-dark via-lr-dark-secondary to-lr-dark flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-card border border-border rounded-lg shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-lr-cyan/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-lr-cyan" />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-2">
              Password Reset Successful!
            </h1>
            <p className="text-muted-foreground mb-6">
              Your password has been updated. Redirecting to login...
            </p>
            <Link to="/auth">
              <Button variant="lr-cyan" className="w-full">
                Go to Login
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Password reset form
  return (
    <div className="min-h-screen bg-gradient-to-br from-lr-dark via-lr-dark-secondary to-lr-dark flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-card border border-border rounded-lg shadow-xl p-8">
          {/* Header */}
          <div className="mb-8 text-center">
            <Link to="/" className="inline-block mb-6">
              <img 
                src={theme === 'dark' ? lanerunnerLogoWhite : lanerunnerLogoBlack} 
                alt="LaneRunner" 
                className="h-10 mx-auto"
              />
            </Link>
            <h1 className="text-2xl font-bold text-foreground mb-2">
              Create New Password
            </h1>
            <p className="text-muted-foreground">
              Enter your new password below
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="password">New Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 pr-12"
                  required
                  autoFocus
                  minLength={8}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Must be at least 8 characters long
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="h-12 pr-12"
                  required
                  minLength={8}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8"
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full h-12" 
              disabled={isLoading}
              variant="lr-cyan"
            >
              {isLoading ? 'Resetting Password...' : 'Reset Password'}
            </Button>
          </form>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6">
          LaneRunner © 2024. All Rights Reserved
        </p>
      </div>
    </div>
  );
}
